import { Link } from '@inertiajs/react';
import { cn } from '../../lib/cn';

const ChevronLeft = () => (
  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
  </svg>
);

const ChevronRight = () => (
  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
  </svg>
);

function HwPill({ lesson, isCurrent, hasActivities, needsReviewCount }) {
  const base = 'text-[10px] leading-none whitespace-nowrap font-semibold px-1.5 py-1 rounded';

  // No HW activity — render an inert dashed pill
  if (!hasActivities) {
    return (
      <span className={cn(base, 'text-gray-300 dark:text-gray-600 border border-dashed border-gray-200 dark:border-gray-700')}>
        HW
      </span>
    );
  }

  const href = `/teacher/activities/${lesson.hw_activity_id}/submissions`;
  const hasNeedsReview = needsReviewCount > 0;

  // Determine style: current (blue) > needs-review (amber) > default (gray)
  let pillClass;
  if (isCurrent) {
    pillClass = 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300';
  } else if (hasNeedsReview) {
    pillClass = 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400';
  } else {
    pillClass = 'bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700';
  }

  return (
    <Link href={href} className={cn(base, pillClass, 'transition-colors')}>
      HW
    </Link>
  );
}

function LessonPill({ lesson, courseId, unitId, isCurrent }) {
  const href = `/teacher/courses/${courseId}/units/${unitId}/lessons/${lesson.id}`;
  const base = 'text-[10px] leading-none whitespace-nowrap font-medium px-2 py-1 rounded transition-colors';

  if (isCurrent) {
    return (
      <Link
        href={href}
        className={cn(base, 'bg-blue-600 text-white dark:bg-blue-500')}
      >
        {lesson.short_title}
      </Link>
    );
  }

  return (
    <Link
      href={href}
      className={cn(base, 'border border-gray-300 bg-gray-50 text-gray-700 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700')}
    >
      {lesson.short_title}
    </Link>
  );
}

function UnitNav({ unit, courseId, direction }) {
  if (!unit) {
    return (
      <span className="text-gray-200 dark:text-gray-700 flex-shrink-0 flex items-center gap-0.5">
        {direction === 'prev' && <ChevronLeft />}
        <span className="text-[10px] leading-none whitespace-nowrap">
          {direction === 'prev' ? '' : ''}
        </span>
        {direction === 'next' && <ChevronRight />}
      </span>
    );
  }

  const href = `/teacher/courses/${courseId}/units/${unit.id}`;

  return (
    <Link
      href={href}
      className="flex-shrink-0 flex items-center gap-0.5 text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 transition-colors"
    >
      {direction === 'prev' && <ChevronLeft />}
      <span className="text-[10px] leading-none whitespace-nowrap font-medium max-w-[6rem] truncate">
        {unit.title}
      </span>
      {direction === 'next' && <ChevronRight />}
    </Link>
  );
}

/**
 * CompactUnitView - shows all lessons and HW slots in the current unit.
 *
 * Props shape (unit_context from controller):
 * {
 *   unit_id, unit_title, course_id,
 *   current_lesson_id,
 *   current_hw_slot: 'homework' | 'prior_homework' | null,
 *   prev_unit: { id, title } | null,
 *   next_unit: { id, title } | null,
 *   lessons: [{ id, title, short_title, position, hw_count, hw_activity_id, hw_needs_review_count }]
 * }
 */
export default function CompactUnitView({ unitContext }) {
  if (!unitContext) return null;

  const {
    unit_id,
    unit_title,
    unit_position,
    course_id,
    current_lesson_id,
    current_hw_slot,
    prev_unit,
    next_unit,
    lessons = [],
  } = unitContext;

  return (
    <div className="mb-4 text-center">
      <Link
        href={`/teacher/courses/${course_id}/units/${unit_id}`}
        className="text-xs font-medium text-gray-500 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
      >
        Unit {unit_position}: {unit_title}
      </Link>

      <div className="flex items-center justify-center gap-1 mt-1.5 flex-wrap">
        <UnitNav unit={prev_unit} courseId={course_id} direction="prev" />

        {lessons.map((lesson, i) => {
          const isCurrentLesson = lesson.id === current_lesson_id;
          // The HW pill after each lesson represents that lesson's homework slot.
          // It's "current" if this lesson is the current one AND the current slot is homework,
          // OR if the *next* lesson is current and slot is prior_homework.
          const nextLesson = lessons[i + 1];
          const isCurrentHw =
            (isCurrentLesson && current_hw_slot === 'homework') ||
            (nextLesson && nextLesson.id === current_lesson_id && current_hw_slot === 'prior_homework');

          return (
            <div key={lesson.id} className="flex items-center gap-1">
              <LessonPill
                lesson={lesson}
                courseId={course_id}
                unitId={unit_id}
                isCurrent={isCurrentLesson && !current_hw_slot}
              />
              <HwPill
                lesson={lesson}
                isCurrent={isCurrentHw}
                hasActivities={lesson.hw_count > 0}
                needsReviewCount={lesson.hw_needs_review_count || 0}
              />
            </div>
          );
        })}

        <UnitNav unit={next_unit} courseId={course_id} direction="next" />
      </div>
    </div>
  );
}
